import { RoleCode, hasRole, type RoleCodeValue } from "./rbac";
import type { VerticalCodeValue } from "../api/org";

export function normalizeRoleCodes(raw: unknown): RoleCodeValue[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((value) => Number(value))
    .filter(
      (value): value is RoleCodeValue =>
        Number.isInteger(value) && value >= RoleCode.SUPER_ADMIN && value <= RoleCode.AI_AGENT,
    );
}

/** Master Blueprint sidebar modules (0-based role gates). */
export interface NavItemDef {
  id: string;
  to: string;
  labelKey: string;
  end?: boolean;
  /** User must hold at least one of these roles (0-based). */
  roles: RoleCodeValue[];
  /** When set, item is visible only for matching vertical_code values. */
  verticalCodes?: VerticalCodeValue[];
}

/** Indexed sidebar sections (Mini-style grouping). */
export interface NavGroupDef {
  id: string;
  labelKey: string;
  itemIds: string[];
  /** Expanded by default when no route matches a child. */
  defaultOpen?: boolean;
}

export const NAV_GROUPS: NavGroupDef[] = [
  {
    id: "home",
    labelKey: "nav.group.home",
    itemIds: ["dashboard"],
    defaultOpen: true,
  },
  {
    id: "sales_ops",
    labelKey: "nav.group.sales_ops",
    itemIds: [
      "sales",
      "purchases",
      "inventory",
      "pos",
      "pos_cashier",
      "crm",
      "shipping",
      "inventory_valuation",
      "stock_count",
    ],
    defaultOpen: true,
  },
  {
    id: "finance",
    labelKey: "nav.group.finance",
    itemIds: [
      "gl",
      "coa",
      "journal_entries",
      "reports",
      "aging",
      "party_statement",
      "bank_recon",
      "vat_return",
      "zakat",
      "cash_flow",
      "accounting_periods",
      "profitability",
      "fixed_assets",
    ],
  },
  {
    id: "production",
    labelKey: "nav.group.production",
    itemIds: ["production_hub", "production_bom", "production_work_orders"],
  },
  {
    id: "hr",
    labelKey: "nav.group.hr",
    itemIds: ["hr", "attendance", "payroll", "payroll_settings", "zkteco"],
  },
  {
    id: "intelligence",
    labelKey: "nav.group.intelligence",
    itemIds: ["ai", "fadvisor"],
  },
  {
    id: "system",
    labelKey: "nav.group.system",
    itemIds: ["settings", "directories", "billing", "data_migration"],
  },
];

export interface NavGroupWithItems {
  id: string;
  labelKey: string;
  items: NavItemDef[];
}

export const MASTER_NAV: NavItemDef[] = [
  {
    id: "dashboard",
    to: "/dashboard",
    labelKey: "nav.dashboard",
    end: true,
    roles: [
      RoleCode.SUPER_ADMIN,
      RoleCode.FINANCIAL_ADVISOR,
      RoleCode.HR_MANAGER,
      RoleCode.SALES,
      RoleCode.DESKTOP_AGENT,
      RoleCode.AI_AGENT,
    ],
  },
  {
    id: "gl",
    to: "/gl",
    labelKey: "nav.gl",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR, RoleCode.AI_AGENT],
  },
  {
    id: "coa",
    to: "/coa",
    labelKey: "nav.coa",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "shipping",
    to: "/shipping",
    labelKey: "nav.shipping",
    roles: [
      RoleCode.SUPER_ADMIN,
      RoleCode.FINANCIAL_ADVISOR,
      RoleCode.SALES,
    ],
  },
  {
    id: "crm",
    to: "/crm",
    labelKey: "nav.crm",
    roles: [
      RoleCode.SUPER_ADMIN,
      RoleCode.FINANCIAL_ADVISOR,
      RoleCode.SALES,
      RoleCode.AI_AGENT,
    ],
  },
  {
    id: "inventory",
    to: "/inventory",
    labelKey: "nav.inventory",
    roles: [
      RoleCode.SUPER_ADMIN,
      RoleCode.SALES,
      RoleCode.DESKTOP_AGENT,
      RoleCode.AI_AGENT,
    ],
  },
  {
    id: "production_hub",
    to: "/production",
    labelKey: "nav.production",
    roles: [
      RoleCode.SUPER_ADMIN,
      RoleCode.FINANCIAL_ADVISOR,
      RoleCode.SALES,
      RoleCode.AI_AGENT,
    ],
    verticalCodes: [1],
  },
  {
    id: "production_bom",
    to: "/production/bom",
    labelKey: "nav.bom",
    roles: [
      RoleCode.SUPER_ADMIN,
      RoleCode.FINANCIAL_ADVISOR,
      RoleCode.SALES,
      RoleCode.AI_AGENT,
    ],
    verticalCodes: [1],
  },
  {
    id: "production_work_orders",
    to: "/production/work-orders",
    labelKey: "nav.work_orders",
    roles: [
      RoleCode.SUPER_ADMIN,
      RoleCode.FINANCIAL_ADVISOR,
      RoleCode.SALES,
      RoleCode.AI_AGENT,
    ],
    verticalCodes: [1],
  },
  {
    id: "fixed_assets",
    to: "/fixed-assets",
    labelKey: "nav.fixed_assets",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR, RoleCode.AI_AGENT],
  },
  {
    id: "sales",
    to: "/sales",
    labelKey: "nav.sales",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.SALES],
  },
  {
    id: "purchases",
    to: "/purchases",
    labelKey: "nav.purchases",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR, RoleCode.SALES],
  },
  {
    id: "hr",
    to: "/hr",
    labelKey: "nav.hr",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.HR_MANAGER],
  },
  {
    id: "attendance",
    to: "/hr/attendance",
    labelKey: "nav.attendance",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.HR_MANAGER],
  },
  {
    id: "payroll",
    to: "/payroll",
    labelKey: "nav.payroll",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.HR_MANAGER],
  },
  {
    id: "payroll_settings",
    to: "/payroll/settings",
    labelKey: "nav.payroll_settings",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.HR_MANAGER],
  },
  {
    id: "settings",
    to: "/settings",
    labelKey: "nav.settings",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "pos",
    to: "/pos",
    labelKey: "nav.pos",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.SALES, RoleCode.DESKTOP_AGENT],
  },
  {
    id: "pos_cashier",
    to: "/pos/cashier",
    labelKey: "nav.pos_cashier",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.SALES, RoleCode.DESKTOP_AGENT],
  },
  {
    id: "reports",
    to: "/reports",
    labelKey: "nav.reports",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR, RoleCode.AI_AGENT],
  },
  {
    id: "aging",
    to: "/aging",
    labelKey: "nav.aging",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "inventory_valuation",
    to: "/inventory-valuation",
    labelKey: "nav.inventory_valuation",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "stock_count",
    to: "/stock-count",
    labelKey: "nav.stock_count",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "bank_recon",
    to: "/bank-recon",
    labelKey: "nav.bank_recon",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "journal_entries",
    to: "/journal-entries",
    labelKey: "nav.journal_entries",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "vat_return",
    to: "/vat-return",
    labelKey: "nav.vat_return",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "zakat",
    to: "/zakat",
    labelKey: "nav.zakat",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "party_statement",
    to: "/party-statement",
    labelKey: "nav.party_statement",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "cash_flow",
    to: "/cash-flow",
    labelKey: "nav.cash_flow",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "accounting_periods",
    to: "/accounting-periods",
    labelKey: "nav.accounting_periods",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "profitability",
    to: "/profitability",
    labelKey: "nav.profitability",
    roles: [RoleCode.SUPER_ADMIN],
  },
  {
    id: "ai",
    to: "/ai",
    labelKey: "nav.ai_advisor",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "fadvisor",
    to: "/fadvisor",
    labelKey: "nav.fadvisor",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "zkteco",
    to: "/zkteco",
    labelKey: "nav.zkteco",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.HR_MANAGER],
  },
  {
    id: "billing",
    to: "/billing",
    labelKey: "nav.billing",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR],
  },
  {
    id: "data_migration",
    to: "/data-migration",
    labelKey: "nav.data_migration",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR, RoleCode.SALES],
  },
  {
    id: "directories",
    to: "/directories",
    labelKey: "nav.directories",
    roles: [RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR, RoleCode.SALES],
  },
];

const NAV_BY_ID = new Map(MASTER_NAV.map((item) => [item.id, item]));

export function isNavItemVisible(
  item: NavItemDef,
  userRoles: RoleCodeValue[],
  verticalCode: VerticalCodeValue,
): boolean {
  if (!hasRole(userRoles, ...item.roles)) {
    return false;
  }
  if (item.verticalCodes && !item.verticalCodes.includes(verticalCode)) {
    return false;
  }
  return true;
}

export function filterNavItems(
  userRoles: RoleCodeValue[] | unknown,
  verticalCode: VerticalCodeValue,
): NavItemDef[] {
  const roles = normalizeRoleCodes(userRoles);
  return MASTER_NAV.filter((item) =>
    isNavItemVisible(item, roles, verticalCode),
  );
}

export function filterNavGroups(
  userRoles: RoleCodeValue[] | unknown,
  verticalCode: VerticalCodeValue,
): NavGroupWithItems[] {
  const roles = normalizeRoleCodes(userRoles);
  return NAV_GROUPS.map((group) => ({
    id: group.id,
    labelKey: group.labelKey,
    items: group.itemIds
      .map((id) => NAV_BY_ID.get(id))
      .filter(
        (item): item is NavItemDef =>
          item !== undefined && isNavItemVisible(item, roles, verticalCode),
      ),
  })).filter((group) => group.items.length > 0);
}

/** Group id that contains the route, if any. */
export function findNavGroupIdForPath(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  for (const group of NAV_GROUPS) {
    for (const itemId of group.itemIds) {
      const item = NAV_BY_ID.get(itemId);
      if (!item) continue;
      const base = item.to.replace(/\/+$/, "") || "/";
      if (item.end ? normalized === base : normalized === base || normalized.startsWith(`${base}/`)) {
        return group.id;
      }
    }
  }
  return null;
}

export function getNavItemById(moduleId: string): NavItemDef | undefined {
  return MASTER_NAV.find((item) => item.id === moduleId);
}

export function canAccessModule(
  moduleId: string,
  userRoles: RoleCodeValue[],
  verticalCode: VerticalCodeValue,
): boolean {
  if (moduleId === "production") {
    const bom = getNavItemById("production_bom");
    const wo = getNavItemById("production_work_orders");
    return (
      (bom ? isNavItemVisible(bom, userRoles, verticalCode) : false) ||
      (wo ? isNavItemVisible(wo, userRoles, verticalCode) : false)
    );
  }
  const item = getNavItemById(moduleId);
  if (!item) return false;
  return isNavItemVisible(item, userRoles, verticalCode);
}
